import { useCallback, useEffect, useRef, useState } from "react";

interface UseViewOnlyModeAlertConfig {
	autoShow?: boolean;
	autoHideDelay?: number;
	persistOnClose?: boolean;
}

const STORAGE_KEY = "view-only-mode-alert-dismissed";

export const useViewOnlyModeAlert = (config: UseViewOnlyModeAlertConfig = {}) => {
	const { autoShow = false, autoHideDelay, persistOnClose = true } = config;
	const initializedRef = useRef(false);

	// Initialize dismissed state from localStorage only once
	const [isDismissed, setIsDismissed] = useState(() => {
		if (persistOnClose) {
			return localStorage.getItem(STORAGE_KEY) === "true";
		}

		return false;
	});

	const [isVisible, setIsVisible] = useState(false);

	// Auto show functionality (runs only once on mount)
	useEffect(() => {
		if (!initializedRef.current) {
			initializedRef.current = true;
			if (autoShow && !isDismissed) {
				setIsVisible(true);
			}
		}
	}, []); // Empty dependency array for one-time initialization

	// Auto hide functionality
	useEffect(() => {
		if (isVisible && autoHideDelay) {
			const timer = setTimeout(() => {
				setIsVisible(false);
			}, autoHideDelay);

			return () => clearTimeout(timer);
		}
	}, [isVisible, autoHideDelay]);

	const showAlert = useCallback(() => {
		if (!isDismissed) {
			setIsVisible(true);
		}
	}, [isDismissed]);

	const hideAlert = useCallback(() => {
		setIsVisible(false);
	}, []);

	const dismissAlert = useCallback(() => {
		setIsVisible(false);
		setIsDismissed(true);

		if (persistOnClose) {
			localStorage.setItem(STORAGE_KEY, "true");
		}
	}, [persistOnClose]);

	const resetDismissal = useCallback(() => {
		setIsDismissed(false);
		if (persistOnClose) {
			localStorage.removeItem(STORAGE_KEY);
		}
	}, [persistOnClose]);

	return {
		isVisible: isVisible && !isDismissed,
		isDismissed,
		showAlert,
		hideAlert,
		dismissAlert,
		resetDismissal,
	};
};
